// v70 `positionRewardMenu` (prototype index.html:2564), ported 1:1 so the
// flip/clamp rule is unit-testable without layout. The menu is fixed to the
// viewport, so it can't be scrolled into view: whichever side it opens on
// must fit on screen.
import type { CSSProperties } from 'react'

export const REWARD_MENU_GAP_PX = 6
export const REWARD_MENU_VIEWPORT_MARGIN_PX = 16
// Open down whenever at least this much room is below, even if above is roomier.
export const REWARD_MENU_COMFORTABLE_SPACE_PX = 180
export const REWARD_MENU_MIN_HEIGHT_PX = 120
export const REWARD_MENU_MAX_HEIGHT_PX = 320

export interface RewardMenuAnchorRect {
  top: number
  bottom: number
  left: number
  width: number
}

interface RewardMenuBase {
  left: number
  width: number
  maxHeight: number
}

// 'up' anchors by `bottom` so the list grows upward from the input as
// results filter down, instead of leaving a gap.
export type RewardMenuPosition =
  | (RewardMenuBase & { placement: 'down'; top: number })
  | (RewardMenuBase & { placement: 'up'; bottom: number })

const clampMenuHeight = (space: number) =>
  Math.max(
    REWARD_MENU_MIN_HEIGHT_PX,
    Math.min(REWARD_MENU_MAX_HEIGHT_PX, space)
  )

export function computeRewardMenuPosition(
  rect: RewardMenuAnchorRect,
  viewportHeight: number
): RewardMenuPosition {
  const reserved = REWARD_MENU_GAP_PX + REWARD_MENU_VIEWPORT_MARGIN_PX
  const spaceBelow = viewportHeight - rect.bottom - reserved
  const spaceAbove = rect.top - reserved
  const base = { left: rect.left, width: rect.width }

  if (
    spaceBelow >= REWARD_MENU_COMFORTABLE_SPACE_PX ||
    spaceBelow >= spaceAbove
  ) {
    return {
      ...base,
      placement: 'down',
      top: rect.bottom + REWARD_MENU_GAP_PX,
      maxHeight: clampMenuHeight(spaceBelow),
    }
  }
  return {
    ...base,
    placement: 'up',
    bottom: viewportHeight - rect.top + REWARD_MENU_GAP_PX,
    maxHeight: clampMenuHeight(spaceAbove),
  }
}

export function rewardMenuStyle(position: RewardMenuPosition): CSSProperties {
  const anchor =
    position.placement === 'down'
      ? { top: position.top }
      : { bottom: position.bottom }
  return {
    position: 'fixed',
    left: position.left,
    width: position.width,
    maxHeight: position.maxHeight,
    ...anchor,
  }
}
